import React, { useEffect, useMemo, useRef, useState } from "react";
import { InfoTip } from "@/components/InfoTip";
import { toast } from "sonner";
import { Camera, Loader2, Upload, X, Copy, Sparkles, Info, ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import { renderSummary } from "@/lib/renderSummary";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ShareAndPrint } from "@/components/ShareAndPrint";
import { useOnlineStatus } from "@/hooks/useOnlineStatus";
import { useL } from "@/lib/useL";

const MAX_MB = 10;

const PhotoDrop = ({ label, hint, file, onPick, onClear, testid }) => {
  const L = useL();
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const previewUrl = useMemo(() => (file ? URL.createObjectURL(file) : null), [file]);
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(L(`Image trop lourde (max ${MAX_MB} Mo).`, `Image too large (max ${MAX_MB} MB).`));
      return;
    }
    onPick(f);
    if (cameraRef.current) cameraRef.current.value = "";
    if (galleryRef.current) galleryRef.current.value = "";
  };

  return (
    <div className="flex flex-col gap-2">
      <label className="sux-marker">{label}</label>
      {!file ? (
        <div
          data-testid={testid}
          className="border border-dashed border-border p-5 flex flex-col items-center justify-center gap-3 min-h-[180px]"
        >
          <Camera className="w-6 h-6 text-[#002FA7]" />
          <span className="font-mono uppercase tracking-[0.14em] text-[11px] text-center">
            {hint}
          </span>
          <div className="flex flex-col sm:flex-row gap-2 w-full mt-1">
            <button
              type="button"
              onClick={() => cameraRef.current?.click()}
              data-testid={testid + "-camera"}
              className="flex-1 bg-[#002FA7] text-white font-mono uppercase tracking-[0.14em] text-[11px] px-3 py-2.5 hover:bg-[#00227A] transition-colors flex items-center justify-center gap-2"
            >
              <Camera className="w-3.5 h-3.5" />
              {L("Prendre une photo", "Take a photo")}
            </button>
            <button
              type="button"
              onClick={() => galleryRef.current?.click()}
              data-testid={testid + "-gallery"}
              className="flex-1 border border-border font-mono uppercase tracking-[0.14em] text-[11px] px-3 py-2.5 hover:bg-secondary/60 transition-colors flex items-center justify-center gap-2"
            >
              <ImageIcon className="w-3.5 h-3.5" />
              {L("Galerie", "Gallery")}
            </button>
          </div>
          <span className="text-xs text-muted-foreground">JPG · PNG · HEIC · WEBP · &lt; {MAX_MB} {L("Mo", "MB")}</span>
        </div>
      ) : (
        <div className="relative border border-border p-2 bg-secondary/30">
          <img
            src={previewUrl}
            alt={label}
            className="w-full max-h-56 object-contain"
          />
          <button
            type="button"
            onClick={onClear}
            data-testid={testid + "-clear"}
            className="absolute top-3 right-3 bg-background border border-border hover:bg-[#FF2A00] hover:text-white transition-colors p-1.5"
            title={L("Retirer", "Remove")}
          >
            <X className="w-3.5 h-3.5" />
          </button>
          <div className="mt-2 sux-marker truncate">{file.name}</div>
        </div>
      )}
      {/* Camera input: capture="environment" forces the rear camera on mobile */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
      />
      {/* Gallery input: no capture attribute → OS gallery picker */}
      <input
        ref={galleryRef}
        type="file"
        accept="image/*,.heic,.heif"
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
      />
    </div>
  );
};

export const BookScanPanel = ({ model, language, register = "auto", audienceLevel }) => {
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);
  const [title, setTitle] = useState("");
  const [author, setAuthor] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const online = useOnlineStatus();
  const L = useL();

  const canSubmit = !busy && online && (front || back || (title.trim().length >= 2));

  const onScan = async () => {
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      if (front) fd.append("front_image", front);
      if (back) fd.append("back_image", back);
      if (title.trim()) fd.append("title", title.trim());
      if (author.trim()) fd.append("author", author.trim());
      fd.append("language", language || "auto");
      fd.append("model", model || "claude-sonnet-4-5");
      fd.append("register", register || "auto");
      if (audienceLevel && audienceLevel !== "aucun") fd.append("audience_level", audienceLevel);
      const { data } = await api.post("/scan/book", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.detected_from_cover) {
        toast.success(
          `${L("Livre reconnu :", "Book recognised:")} ${data.title}${data.author ? " — " + data.author : ""}`
        );
      } else {
        toast.success(L("Fiche de lecture générée.", "Reading sheet generated."));
      }
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        L("Impossible de générer la fiche pour ce livre.", "Could not generate the sheet for this book.");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const onReset = () => {
    setFront(null);
    setBack(null);
    setTitle("");
    setAuthor("");
    setResult(null);
  };

  const onCopy = async () => {
    if (!result?.fiche) return;
    await navigator.clipboard.writeText(result.fiche);
    toast.success(L("Fiche copiée dans le presse-papiers.", "Sheet copied to clipboard."));
  };

  return (
    <div className="flex-1 p-6 md:p-8 lg:p-10 flex flex-col gap-6">
      <div className="border border-[#002FA7]/30 bg-[#002FA7]/5 p-4 flex items-start gap-3">
        <Info className="w-4 h-4 text-[#002FA7] mt-0.5 shrink-0" />
        <div className="text-sm leading-relaxed">
          <div className="font-semibold mb-1">{L("Scanner un livre", "Scan a book")}</div>
          <p className="text-muted-foreground">
            {L(<>Prenez en photo la <strong>couverture</strong> (et si possible la <strong>4e de couverture</strong>). SUX1 reconnaît le livre et crée une fiche de lecture à partir des informations publiques sur l&apos;œuvre. Vos photos ne sont pas conservées.{" "}</>,
               <>Take a photo of the <strong>front cover</strong> (and if possible the <strong>back cover</strong>). SUX1 recognises the book and creates a reading sheet from public information about the work. Your photos are not kept.{" "}</>)}
            <span className="text-[#002FA7] font-medium">{L("1 crédit par fiche.", "1 credit per sheet.")}</span>
            <span className="block mt-2 text-xs">
              {L(<>Astuce vitesse : <strong>Gemini 3 Flash</strong> répond en ~10 s, <strong>Claude Sonnet 4.5</strong> est plus détaillé mais prend 30 à 60 s.</>,
                 <>Speed tip: <strong>Gemini 3 Flash</strong> answers in ~10 s, <strong>Claude Sonnet 4.5</strong> is more detailed but takes 30 to 60 s.</>)}
            </span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <PhotoDrop
          label={L("1 · Couverture", "1 · Front cover")}
          hint={L("Photographier la couverture", "Photograph the front cover")}
          file={front}
          onPick={setFront}
          onClear={() => setFront(null)}
          testid="book-scan-front"
        />
        <PhotoDrop
          label={L("2 · 4e de couverture (facultatif)", "2 · Back cover (optional)")}
          hint={L("Ajouter la 4e de couverture", "Add the back cover")}
          file={back}
          onPick={setBack}
          onClear={() => setBack(null)}
          testid="book-scan-back"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-2">
          <label className="sux-marker flex items-center gap-1">{L("Titre (si la photo ne suffit pas)", "Title (if the photo isn't enough)")} <InfoTip testid="book-scan-manual-info" content={L("Facultatif. Si la photo est floue ou la couverture illisible, tapez le titre et l'auteur : SUX1 s'en sert pour trouver le bon livre. Vous pouvez aussi taper le titre sans aucune photo.", "Optional. If the photo is blurry or the cover unreadable, type the title and author: SUX1 uses them to find the right book. You can also type the title with no photo at all.")} /></label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={L("Ex. Le Petit Prince", "E.g. The Little Prince")}
            data-testid="book-scan-title"
            className="w-full border border-border bg-card px-4 py-3 text-[15px] focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label className="sux-marker">{L("Auteur (facultatif)", "Author (optional)")}</label>
          <input
            type="text"
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            placeholder={L("Ex. Antoine de Saint-Exupéry", "E.g. Antoine de Saint-Exupéry")}
            data-testid="book-scan-author"
            className="w-full border border-border bg-card px-4 py-3 text-[15px] focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]"
          />
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={onScan}
          disabled={!canSubmit}
          data-testid="book-scan-submit"
          className="bg-[#002FA7] text-white font-mono uppercase tracking-[0.18em] text-[12px] px-6 py-3 hover:bg-[#00227A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
        >
          {busy ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> {L("Analyse en cours (30 à 60 s)…", "Analysing (30 to 60 s)…")}
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" /> {L("Générer la fiche", "Generate the sheet")}
            </>
          )}
        </button>
        {(front || back || title || author || result) && (
          <button
            onClick={onReset}
            data-testid="book-scan-reset"
            className="font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-3 border border-border text-muted-foreground hover:text-[#FF2A00] hover:border-[#FF2A00] transition-colors"
          >
            {L("Recommencer", "Start over")}
          </button>
        )}
      </div>

      {result && (
        <section className="border border-border bg-card mt-2">
          <div className="p-6 md:p-8 border-b border-border flex items-start justify-between flex-wrap gap-3">
            <div>
              <div className="sux-marker mb-2">{L("Fiche de lecture", "Reading sheet")}</div>
              <h3
                className="text-2xl md:text-3xl font-black tracking-tight"
                style={{ fontFamily: "Outfit" }}
              >
                {result.title}
              </h3>
              {result.author && (
                <p className="text-sm text-muted-foreground mt-1">
                  {L("par", "by")} {result.author}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onCopy}
                data-testid="book-scan-copy"
                className="font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-2 border border-border hover:bg-[#FF2A00] hover:text-white hover:border-[#002FA7] transition-colors flex items-center gap-2"
              >
                <Copy className="w-3.5 h-3.5" /> {L("Copier", "Copy")}
              </button>
              <ShareAndPrint
                title={result.title + (result.author ? " — " + result.author : "")}
                text={result.fiche}
                url={null}
                testidPrefix="book"
              />
            </div>
          </div>
          <div className="p-6 md:p-8">
            <ErrorBoundary
              fallback={(msg, reset) => (
                <div className="border border-[#FF2A00] bg-[#FF2A00]/5 p-4 text-sm">
                  <pre className="font-mono text-[11px] whitespace-pre-wrap" translate="no">
                    {String(result.fiche || "")}
                  </pre>
                  <button
                    onClick={reset}
                    className="mt-2 font-mono uppercase tracking-[0.14em] text-[11px] px-3 py-2 border border-[#FF2A00] text-[#FF2A00]"
                  >
                    {L("Réessayer", "Try again")}
                  </button>
                </div>
              )}
            >
              <article
                data-testid="book-scan-result"
                className="summary-content max-w-3xl notranslate"
                translate="no"
                dangerouslySetInnerHTML={{ __html: renderSummary(result.fiche) }}
              />
            </ErrorBoundary>
            <p className="mt-6 max-w-3xl text-xs text-muted-foreground border-t border-border pt-4 italic">
              {L("Fiche générée par IA à partir des informations publiques sur ce livre. SUX1 ne reproduit pas le texte de l'œuvre. Vérifiez toujours une information importante avant de la citer.",
                 "AI-generated sheet based on public information about this book. SUX1 does not reproduce the text of the work. Always check important information before quoting it.")}
            </p>
          </div>
        </section>
      )}
    </div>
  );
};

export default BookScanPanel;
